import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpClient } from '@angular/common/http';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as OrdersActions from '../../actions/account/actions-orders';
import {
  FilterOrdersByDateSuccess,
  GetOrdersSuccess,
  ActionsOrders,
  SortingPayload
} from '../../actions/account/actions-orders';
import { Observable, of } from 'rxjs';
import {
  compareByProperty,
  filterByDateAndOfficeCode,
  filterByHalfYearDate,
  getSearchedOrders,
  BackendAccountService
} from '../../services/backend-account.service';
import { IOrderModel, TimePeriodsFilters } from '../../models/account/IOrderModel';
import { OrdersState } from '../../models/account/orders.state';
import { getOrdersInfo } from '../../reducers';
import { select, Store } from '@ngrx/store';
import { compareByPropertyAsc } from '../../services/utils/compare.utils';
import { HeaderAlertService } from '@uxdf/direct-shared-lib';
import * as CoreActions from '@uxdf/direct-shared-lib/src/lib/login/core.actions';

@Injectable()
export class EffectsOrders {
  filterOrdersByOfficeCode: Observable<ActionsOrders> = createEffect(() => {
    return this.actions$.pipe(
      ofType<OrdersActions.FilterOrdersByOfficeCode>(OrdersActions.FILTER_ORDERS_BY_OFFICE_CODE),
      map(action => action.payload),
      withLatestFrom(this.store.pipe(select(getOrdersInfo))),
      switchMap(([officeCode, state]: [string, OrdersState]) =>
        filterByDateAndOfficeCode(state.orders, state.timePeriodFilter, officeCode).pipe(
          switchMap(result => [new OrdersActions.GetOrdersSuccess(result)])
        )
      )
    )
  });

  //
  // @Effect()
  // getOrdersConfig$: Observable<ActionsOrders> = this.actions$.pipe(
  //   ofType<OrdersActions.GetOrdersConfig>(OrdersActions.GET_ORDERS_CONFIG),
  //   withLatestFrom(this.store.pipe(select(customerConfig))),
  //   switchMap(([action, customerConfigState]: [OrdersActions.GetOrdersConfig, any]) => {
  //       const customerConfig: Customer = customerConfigState.customerConfig;
  //       return this.backendAccountService.getMagentoOrdersConfig(customerConfig.default_store_code).pipe(
  //         map((data) => {
  //           return new OrdersActions.GetOrdersConfigSuccess(data);
  //         })
  //       );
  //     }
  //   )
  // );
  //
  // @Effect()
  // getOrdersConfigSuccess$: Observable<ActionsOrders> = this.actions$.pipe(
  //   ofType<OrdersActions.GetOrdersConfigSuccess>(OrdersActions.GET_ORDERS_CONFIG_SUCCESS),
  //   map(() => {
  //       return new OrdersActions.GetOrders();
  //     }
  //   )
  // );

  getOrders$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.GET_ORDERS),
      switchMap(() =>
        this.backendAccountService.getOrders().pipe(
          map((data: IOrderModel[]) => {
            data.sort((a, b) => compareByPropertyAsc(a, b, 'increment_id'));
            data.forEach((o /* cnt */) => {
              o.shown = false;
              o.expanded = false;
            });

            const officeId = JSON.parse(localStorage.getItem('login')).officeId;
            const searchedOrders = filterByHalfYearDate(data);

            return new OrdersActions.GetOrdersSuccess({
              allOrders: data,
              searchedOrders,
              timePeriod: TimePeriodsFilters.lastSixMonths,
              officesFilter: officeId
            });

            // "ALL"
          }),
          catchError(error => {
            if (error && error.error !== undefined && (error.error.code === 40 || error.error.code === 42)) {
              return of(new CoreActions.DirectUserRoleHasBeenChanged());
            } else {
              this.alert.add({
                type: 'error',
                content: $localize`:@@effects-orders.pageSomethingwentwrongHeading:Oops... Something went wrong`
              });
              return of(new OrdersActions.GetOrdersError());
            }
          })
        )
      )
    )
  });

  searchOrders: Observable<GetOrdersSuccess> = createEffect(() => {
    return this.actions$.pipe(
      ofType<OrdersActions.SearchOrders>(OrdersActions.SEARCH_ORDERS),
      map(action => action.payload),
      withLatestFrom(this.store.pipe(select(getOrdersInfo))),
      switchMap(([searchedString, state]) =>
        of(
          new OrdersActions.GetOrdersSuccess(
            getSearchedOrders(searchedString, state.orders, state.timePeriodFilter, state.officesFilter)
          )
        )
      )
    )
  });

  filterOrderByDate: Observable<FilterOrdersByDateSuccess> = createEffect(() => {
    return this.actions$.pipe(
      ofType<OrdersActions.FilterOrdersByDate>(OrdersActions.FILTER_ORDERS_BY_DATE),
      map(action => action.payload),
      withLatestFrom(this.store.pipe(select(getOrdersInfo))),
      switchMap(([dateType, state]) =>
        filterByDateAndOfficeCode(state.orders, dateType, state.officesFilter).pipe(
          switchMap(result => [new OrdersActions.FilterOrdersByDateSuccess(result)])
        )
      )
    )
  });

  sortOrdersByProperty: Observable<OrdersActions.SortOrdersByPropertySuccess> = createEffect(() => {
    return this.actions$.pipe(
      ofType<OrdersActions.SortOrdersByProperty>(OrdersActions.SORT_ORDERS_BY_PROPERTY),
      map(action => action.payload),
      withLatestFrom(this.store.pipe(select(getOrdersInfo))),
      switchMap(([payload, ordersState]: [SortingPayload, OrdersState]) => {
        const searchedOrders = ordersState.searchedOrders;
        const sortedOrders = compareByProperty(payload.sorting, payload.sortingProperty, searchedOrders);

        return of(new OrdersActions.SortOrdersByPropertySuccess(sortedOrders));
      })
    )
  });

  changePages: Observable<any> = createEffect(() => {
    return this.actions$.pipe(
      ofType<OrdersActions.SortOrdersByPropertySuccess>(OrdersActions.SORT_ORDERS_BY_PROPERTY_SUCCESS),
      map(action => action.payload),
      withLatestFrom(this.store.pipe(select(getOrdersInfo))),
      switchMap(([, /* payload */ ordersState]: [any, OrdersState]) =>
        of(new OrdersActions.ChangePage(ordersState.currentPage))
      )
    )
  });

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly backendAccountService: BackendAccountService,
    private readonly store: Store<OrdersState>,
    private readonly alert: HeaderAlertService
  ) { }
}
